import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';
import styled, { keyframes } from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import parseMetadata from '../../utils/metadata';
import { extractDomain, getFavIcon } from '../../utils/favIcon';
import { LinkTooltipProps, Metadata } from '../../interfaces/components/linkTooltip';

const pulse = keyframes`
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
`;

const reveal = keyframes`
    from {
        clip-path: inset(0 100% 0 0);
    }
    to {
        clip-path: inset(0 0 0 0);
    }
`;

/* ----------------- Styled Components ----------------- */

const StyledLink = styled.a`
  display: inline;
  color: ${({ theme }) => theme.primary};
  text-decoration: none;
  white-space: normal;
  word-break: break-word;
  word-wrap: break-word;

  &:hover {
    color: ${({ theme }) => theme.secondary};
  }
`;

// New styled component for medical source links with subtle underline
const MedicalSourceStyledLink = styled.a`
  display: inline;
  text-decoration: none;
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
  position: relative;
  padding: 0 2px;

  /* Background gradient setup for the underline effect */
  background-image: linear-gradient(${({ theme }) => theme.primary}40, ${({ theme }) => theme.primary}40);
  background-size: 100% 2px;
  background-position: 0 100%;
  background-repeat: no-repeat;
  transition: background-size 0.3s ease-in-out;

  &:hover {
    background-size: 100% 100%;
  }
`;

// Additionally, let's modify the BasedOnWrapper component to better control the layout
const BasedOnWrapper = styled.span`
  display: inline;
  white-space: nowrap;
`;

const ContentWrapper = styled.div<{ $state: string }>`
  width: 320px;
  background: ${({ theme }) => theme.tooltip.background};
  border: 0.5px solid #403e3f80;
  border-radius: 8px;
  padding: 16px;
  z-index: 50;
`;

const DomainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Favicon = styled.img`
  height: 24px;
  cursor: pointer;
  top: 4px;
  position: relative;
  object-fit: contain;
`;

const SmallFavicon = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;
  object-fit: contain;
`;

const Domain = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.text.light};
  opacity: 0.5;
`;

const SkeletonWrapper = styled.div`
  width: 320px;
  min-height: 139px;
  padding: 16px;
  background: ${({ theme }) => theme.tooltip.background};
  border-radius: 8px;
`;

const SkeletonItem = styled.div<{ $width?: string; $height?: string }>`
  background: ${({ theme }) => theme.skeletonBackground};
  border-radius: 4px;
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '16px'};
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

export const Title = styled.h3`
  position: relative;
  font-size: ${({ theme }) => theme.text.s8};
  margin: 6px 0 0;
  font-weight: 600;
  color: ${({ theme }) => theme.tooltip.background};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  cursor: pointer;

  &::before,
  &::after {
    content: attr(data-text);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    font: inherit;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &::before {
    z-index: 2;
    color: ${({ theme }) => theme.tooltip.grayText};
    clip-path: inset(0 100% 0 0);
    animation: ${reveal} 0.4s cubic-bezier(0.61, 1, 0.88, 1) forwards;
  }

  &::after {
    z-index: 3;
    color: ${({ theme }) => theme.tooltip.text};
    clip-path: inset(0 100% 0 0);
    animation: ${reveal} 0.7s cubic-bezier(0.61, 1, 0.88, 1) forwards;
    animation-delay: 0.4s;
  }

  &:hover {
    color: ${({ theme }) => theme.primary};
    &::after {
      color: ${({ theme }) => theme.primary};
    }
  }
`;

export const Description = styled.p`
  position: relative;
  font-size: ${({ theme }) => theme.text.s85};
  margin: 0;
  margin-top: 8px;
  color: ${({ theme }) => theme.tooltip.background};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;

  &::before,
  &::after {
    content: attr(data-text);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    font: inherit;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  &::before {
    z-index: 2;
    color: ${({ theme }) => theme.tooltip.grayText};
    clip-path: inset(0 100% 0 0);
    animation: ${reveal} 0.4s cubic-bezier(0.61, 1, 0.88, 1) forwards;
  }

  &::after {
    z-index: 3;
    color: ${({ theme }) => theme.tooltip.descriptionText};
    clip-path: inset(0 100% 0 0);
    animation: ${reveal} 0.7s cubic-bezier(0.61, 1, 0.88, 1) forwards;
    animation-delay: 0.4s;
  }
`;

/* ----------------- LinkTooltip Component ----------------- */

export const LinkTooltip: React.FC<LinkTooltipProps> = ({
  href,
  genioHref,
  children,
  className = '',
  websiteName,
  showName = false,
}) => {
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contentState, setContentState] = useState<'open' | 'closed'>('closed');

  // State to track favicon errors and retries
  const [faviconError, setFaviconError] = useState(false);
  const [hasRetriedFavicon, setHasRetriedFavicon] = useState(false);

  // Separate hover states for each link
  const [isSourceLinkHovered, setIsSourceLinkHovered] = useState(false);
  const [isGenioLinkHovered, setIsGenioLinkHovered] = useState(false);
  const [isSourceTooltipHovered, setIsSourceTooltipHovered] = useState(false);
  const [isGenioTooltipHovered, setIsGenioTooltipHovered] = useState(false);

  // Cache metadata for URLs to prevent re-fetching
  const metadataCache = useRef<Map<string, Metadata>>(new Map());

  // Get the chat layout direction from Redux state
  const layoutDirection = useSelector((state: RootState) => state.chat.selectedChatLayoutDirection);

  // Delay closing the tooltip when not hovered
  useEffect(() => {
    if (!isSourceLinkHovered && !isSourceTooltipHovered) {
      const timer = setTimeout(() => setIsOpen(false), 200);
      return () => clearTimeout(timer);
    } else {
      setIsOpen(true);
    }
  }, [isSourceLinkHovered, isSourceTooltipHovered]);

  // Load metadata only when the tooltip is open and not already loaded
  useEffect(() => {
    let isMounted = true;

    // Skip metadata fetching if href is null/empty or tooltip is not open
    if (!href || !isOpen || metadata || error) {
      return;
    }

    if (metadataCache.current.has(href)) {
      setMetadata(metadataCache.current.get(href)!);
      return;
    }

    const domain = extractDomain(href);
    parseMetadata(href)
      .then((data) => {
        if (isOpen && data && isMounted) {
          const newMetadata: Metadata = {
            title: data.ogTitle || data.title || href,
            description: data.description || data.ogDescription,
            domain,
            favIcon: data.favicon,
          };
          metadataCache.current.set(href, newMetadata);
          setMetadata(newMetadata);
        }
      })
      .catch(() => {
        if (isMounted) {
          setMetadata({
            title: href,
            description: '',
            domain,
            favIcon: '',
          });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [href, isOpen, metadata, error]);

  // Update content state for animations based on isOpen
  useEffect(() => {
    setContentState(isOpen ? 'open' : 'closed');
  }, [isOpen]);

  const fallbackTitle = useMemo(() => (typeof children === 'string' ? children : href), [children, href]);

  // Stable event handlers using useCallback
  const handleSourceMouseEnter = useCallback(() => setIsSourceLinkHovered(true), []);
  const handleSourceMouseLeave = useCallback(() => {
    setTimeout(() => {
      if (!isSourceTooltipHovered) {
        setIsSourceLinkHovered(false);
      }
    }, 100);
  }, [isSourceTooltipHovered]);

  // For medical sources with Genio summary
  if (!!showName && genioHref && websiteName) {
    return (
      <>
        <span style={{ display: 'inline', fontWeight: '500' }}>
          {layoutDirection === 'rtl' ? 'بناءً على' : 'Based on'}
        </span>
        <MedicalSourceStyledLink
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          data-text={websiteName}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
          {href && <SmallFavicon src={getFavIcon(href)} alt="Source Favicon" />}
          {websiteName}
        </MedicalSourceStyledLink>
        <span style={{ display: 'inline', margin: '0 4px' }}>(</span>
        <MedicalSourceStyledLink
          href={genioHref}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          data-text="Genio Summary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
          {genioHref && <SmallFavicon src={getFavIcon(genioHref)} alt="Summary Favicon" />}
          Genio Summary
        </MedicalSourceStyledLink>
        <span style={{ display: 'inline', margin: '0 4px' }}>)</span>
      </>
    );
  }

  // If no href, just render children or 'Ref'
  if (!href) {
    return <span className={className}>{children || 'Ref'}</span>;
  }

  // Regular link tooltip (original implementation)
  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <StyledLink
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          onMouseEnter={handleSourceMouseEnter}
          onMouseLeave={handleSourceMouseLeave}
        >
          {href ? <Favicon src={getFavIcon(href)} alt="Favicon" /> : children || 'Ref'}
        </StyledLink>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={5}
          style={{ outline: 'none', zIndex: 1000 }}
          onInteractOutside={() => setIsOpen(false)}
          onMouseEnter={handleSourceMouseEnter}
          onMouseLeave={handleSourceMouseLeave}
        >
          {error ? (
            // Fallback content for a failed preview:
            <ContentWrapper $state={contentState}>
              <DomainRow>
                {href && <SmallFavicon src={getFavIcon(href)} alt="fav icon" />}
                <Domain>{extractDomain(href)}</Domain>
              </DomainRow>
              <Title onClick={() => window.open(href, '_blank')} data-text={fallbackTitle}>
                {fallbackTitle}
              </Title>
              <Description data-text={''}>{''}</Description>
            </ContentWrapper>
          ) : !metadata ? (
            <SkeletonWrapper>
              <DomainRow>
                {href && <SkeletonItem $width="16px" $height="16px" />}
                <SkeletonItem $width="100px" />
              </DomainRow>
              {/* Skeleton for Title */}
              <SkeletonItem $width="100%" $height="16px" style={{ marginTop: '8px' }} />
              <SkeletonItem $width="75%" $height="16px" style={{ marginTop: '2px' }} />
              {/* Skeleton for Description */}
              <SkeletonItem $width="100%" $height="16px" style={{ marginTop: '8px' }} />
              <SkeletonItem $width="75%" $height="16px" style={{ marginTop: '2px' }} />
            </SkeletonWrapper>
          ) : (
            <ContentWrapper $state={contentState}>
              <DomainRow>
                {href && (
                  <SmallFavicon
                    src={getFavIcon(href)}
                    alt=""
                    onError={(e) => {
                      if (!hasRetriedFavicon) {
                        setFaviconError(true);
                        e.currentTarget.src = 'fallback-favicon.png';
                      }
                    }}
                  />
                )}
                <Domain>{metadata.domain}</Domain>
              </DomainRow>
              <Title onClick={() => window.open(href, '_blank')} data-text={metadata.title}>
                {metadata.title}
              </Title>
              <Description data-text={metadata.description}>{metadata.description}</Description>
            </ContentWrapper>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

// Separate component for Genio Summary Content
const GenioSummaryContent: React.FC<{ genioHref: string }> = ({ genioHref }) => {
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [faviconError, setFaviconError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    const domain = extractDomain(genioHref);

    parseMetadata(genioHref)
      .then((data) => {
        if (isMounted) {
          setMetadata({
            title: data?.ogTitle || data?.title || 'Genio Summary',
            description: data?.description || data?.ogDescription || 'AI-generated summary of the medical source',
            domain,
            favIcon: data?.favicon || '',
          });
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError('Failed to load Genio summary metadata');
          setMetadata({
            title: 'Genio Summary',
            description: 'AI-generated summary of the medical source',
            domain,
            favIcon: '',
          });
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [genioHref]);

  if (loading) {
    return (
      <SkeletonWrapper>
        <DomainRow>
          <SkeletonItem $width="16px" $height="16px" />
          <SkeletonItem $width="100px" />
        </DomainRow>
        {/* Skeleton for Title */}
        <SkeletonItem $width="100%" $height="16px" style={{ marginTop: '8px' }} />
        <SkeletonItem $width="75%" $height="16px" style={{ marginTop: '2px' }} />
        {/* Skeleton for Description */}
        <SkeletonItem $width="100%" $height="16px" style={{ marginTop: '8px' }} />
        <SkeletonItem $width="75%" $height="16px" style={{ marginTop: '2px' }} />
      </SkeletonWrapper>
    );
  }

  if (error || !metadata) {
    return (
      <ContentWrapper $state="open">
        <DomainRow>
          <SmallFavicon src={getFavIcon(genioHref)} alt="" />
          <Domain>{extractDomain(genioHref)}</Domain>
        </DomainRow>
        <Title onClick={() => window.open(genioHref, '_blank')} data-text="Genio Summary">
          Genio Summary
        </Title>
        <Description data-text="AI-generated summary of the medical source">
          AI-generated summary of the medical source
        </Description>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper $state="open">
      <DomainRow>
        <SmallFavicon
          src={getFavIcon(genioHref)}
          alt=""
          onError={(e) => {
            if (!faviconError) {
              setFaviconError(true);
              e.currentTarget.src = 'fallback-favicon.png';
            }
          }}
        />
        <Domain>{metadata.domain}</Domain>
      </DomainRow>
      <Title onClick={() => window.open(genioHref, '_blank')} data-text={metadata.title}>
        {metadata.title}
      </Title>
      <Description data-text={metadata.description}>{metadata.description}</Description>
    </ContentWrapper>
  );
};

export default LinkTooltip;
